import { Coins, HandCoins, Loader2, Ticket, UserPlus } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { cn } from "../../../handlers/cn.utils";
import type { PointsSummary } from "../../../handlers/rewards";
import { currencyFormat } from "../../../handlers/utilityFunctions";
import { useCountUp } from "../../../hooks/useCountUp";
import Burst from "./Burst";

interface PointsPanelProps {
	summary: PointsSummary;
	/** Points the server will release right now, across every reward that is ready. */
	claimablePoints: number;
	collecting: boolean;
	onCollect: () => void;
	onRefer: () => void;
	onRedeem: () => void;
	/** Rises on each collection started from this panel's own button. Zero while another surface owns
	 *  the burst, so the wallet stays still when a row was the thing pressed. */
	celebrations: number;
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "locked" | "spent" }) {
	const shown = useCountUp(value);

	return (
		<div>
			<p className="text-sm text-gray-600 font-inter">{label}</p>
			<p
				className={cn(
					"mt-0.5 text-lg font-bold tabular-nums font-plus-jakarta",
					tone === "locked" ? "text-amber-700" : tone === "spent" ? "text-gray-400" : "text-gray-900",
				)}
			>
				{currencyFormat(shown)} <span className="text-sm font-medium text-gray-400">pts</span>
			</p>
		</div>
	);
}

/**
 * The wallet: what is spendable, what the rest of the balance is doing, and the one thing worth doing
 * about it.
 *
 * Every account arrives here already holding points — the server mints a resume reward on the first
 * read — so the opening state is never empty, it is *locked*. A wallet reading nought would look like
 * a bug on its own, which is what the row underneath answers.
 *
 * The button always carries a live action, in the order the reader gains from it: collect what is
 * already earned, spend what is already collected, then earn more. Collecting leads because it is
 * free money the account is holding and the reader has no reason to know it is there — the alternative
 * was a disabled Redeem sitting above a Collect button several hundred pixels down the table.
 *
 * Referring is the floor rather than finishing a resume, because the resume already has a card of its
 * own in the rail carrying its own progress. Two buttons pointing at the same task, one of them
 * without the percentage that explains it, only splits the ask.
 */
function PointsPanel({
	summary,
	claimablePoints,
	collecting,
	onCollect,
	onRefer,
	onRedeem,
	celebrations,
}: PointsPanelProps) {
	const hasSpendable = summary.available > 0;
	const availableShown = useCountUp(summary.available);

	const action =
		claimablePoints > 0
			? {
					short: "Collect",
					full: `Collect ${currencyFormat(claimablePoints)} points`,
					icon: <HandCoins className="size-4" />,
					onClick: onCollect,
				}
			: hasSpendable
				? { short: "Redeem", full: "Redeem points", icon: <Ticket className="size-4" />, onClick: onRedeem }
				: { short: "Refer", full: "Refer a friend", icon: <UserPlus className="size-4" />, onClick: onRefer };

	return (
		<section aria-label="Your rewards wallet" className="rounded-lg border border-gray-200 bg-white p-6">
			{/* One line at every width: the badge and the figure step down on a phone rather than
			    letting the button wrap under them. */}
			<div className="flex items-center gap-3 sm:gap-4">
				<div className="relative flex size-12 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700 sm:size-14">
					<Coins className="size-5 sm:size-6" />
					{/* Keyed on the count so each collection mounts a fresh burst and replays it. */}
					{celebrations > 0 && <Burst key={celebrations} />}
				</div>

				<div className="min-w-0">
					<p
						className={cn(
							"text-3xl font-extrabold tracking-tight tabular-nums font-plus-jakarta sm:text-4xl",
							hasSpendable ? "text-primary-800" : "text-gray-400",
						)}
					>
						{currencyFormat(availableShown)}
					</p>
					<p className="text-sm text-gray-600 font-inter">Available points</p>
				</div>

				<Button size="sm" className="ml-auto shrink-0" onClick={action.onClick} disabled={collecting}>
					{collecting ? <Loader2 className="size-4 animate-spin" /> : action.icon}
					<span className={cn("sm:hidden", action.icon && "ml-1.5")}>{action.short}</span>
					<span className={cn("hidden sm:inline", action.icon && "ml-1.5")}>{action.full}</span>
				</Button>
			</div>

			{summary.total > 0 && (
				<div className="mt-5 grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-3">
					<Stat label="Pending" value={summary.pending} tone="locked" />
					<Stat label="Redeemed" value={summary.redeemed} tone="spent" />
					<Stat label="Total rewards" value={summary.total} />
				</div>
			)}
		</section>
	);
}

export default PointsPanel;
